import { useState, useCallback, useEffect } from "react";
import { trackAddToCart } from "@/lib/siteIntegrations";
import type { StoreProduct } from "@/mocks/storeProducts";

export interface CartItem {
  product: StoreProduct;
  quantity: number;
}

export function useCart() {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    try { const raw = localStorage.getItem("isman_cart"); return raw ? (JSON.parse(raw) as CartItem[]) : []; } catch { return []; }
  });
  useEffect(() => { try { localStorage.setItem("isman_cart", JSON.stringify(cartItems)); } catch { /* ignore */ } }, [cartItems]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const addToCart = useCallback((product: StoreProduct, qty = 1) => {
    trackAddToCart(product.id, product.name, product.price);
    setCartItems((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, product, quantity: item.quantity + qty }
            : item
        );
      }
      return [...prev, { product, quantity: qty }];
    });
    setIsCartOpen(true);
  }, []);

  const removeFromCart = useCallback((productId: number) => {
    setCartItems((prev) => prev.filter((item) => item.product.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId: number, quantity: number) => {
    if (quantity <= 0) {
      setCartItems((prev) => prev.filter((item) => item.product.id !== productId));
      return;
    }
    setCartItems((prev) =>
      prev.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cartItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);

  return {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    totalItems,
    subtotal,
  };
}
